import React from 'react';

import { Button as HeadlessButton } from '@headlessui/react';

import cn from '~/utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  outline?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  name?: string;
  value?: string;
  form?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  outline = false,
  onClick,
  className = '',
  type = 'button',
  id,
  name,
  value,
  form,
  ariaLabel,
  ariaDescribedby,
}) => {
  // Base variant styles (solid backgrounds)
  const variantBase: Record<ButtonVariant, string> = {
    primary: outline
      ? 'bg-pink-100 border-2 border-pink-600 text-pink-600 hover:bg-pink-200 focus:bg-pink-600 focus:text-pink-100 disabled:bg-pink-100 disabled:text-pink-600'
      : 'bg-pink-600 border-2 border-pink-600 hover:bg-pink-500 hover:border-pink-500 text-white focus:bg-pink-700 disabled:bg-pink-200 disabled:text-pink-500',
    secondary: outline
      ? 'bg-transparent border-2 border-grey-500 text-grey-700 hover:bg-grey-100'
      : 'bg-grey-200 hover:bg-grey-300 text-grey-800',
    success: outline
      ? 'bg-transparent border-2 border-green-600 text-green-700 hover:bg-green-100'
      : 'bg-green-700 hover:bg-green-600 text-white',
    danger: outline
      ? 'bg-transparent border-2 border-red-700 text-red-700 hover:bg-red-100'
      : 'bg-red-700 hover:opacity-80 text-white',
    warning: outline
      ? 'bg-transparent border-2 border-yellow-700 text-yellow-700 hover:bg-yellow-100'
      : 'bg-yellow-700 hover:bg-yellow-800 text-white',
  };

  // Size styles using configured spacing
  const sizes: Record<ButtonSize, string> = {
    xs: 'px-8 py-2 text-sm',
    sm: 'px-8 py-4 text-sm',
    md: 'px-12 py-8 text-base',
    lg: 'px-20 py-12 text-lg',
  };

  const baseStyles = 'rounded-lg font-medium transition-colors cursor-pointer focus:outline-none duration-200';

  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <HeadlessButton
      as="button"
      id={id}
      name={name}
      value={value}
      form={form}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(baseStyles, variantBase[variant], sizes[size], disabledStyles, className)}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
    >
      {children}
    </HeadlessButton>
  );
};

export default Button;
